/**
 * Portal Page - 사용자 포탈
 * 
 * 일반 사용자가 배포된 챗봇을 사용하는 화면입니다.
 * 관리자 화면과 분리된 별도의 레이아웃을 사용합니다.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { marked } from 'marked';
import { nanoid } from 'nanoid';
import { authApi, workflowApi, Workflow, ChatMessage as ApiChatMessage, ChatSource } from '../api';
import { chatSessionApi } from '../api/chat-sessions';
import { PortalSidebar } from '../components/layout';
import { ChartBlock } from '../components/chart/ChartBlock';
import '../styles/chat.css';

// SQL 접힘 블록 컴포넌트
function SqlBlock({ sql }: { sql: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="chat-sql-block">
      <button className="chat-sql-toggle" onClick={() => setOpen(o => !o)}>
        <span>{open ? '▼' : '▶'}</span>
        <span>🔍 생성된 SQL 보기</span>
      </button>
      {open && <pre className="chat-sql-pre">{sql}</pre>}
    </div>
  );
}

// 파비콘 변경 (public/favicon-admin.svg · favicon-user.svg - Acxionon 심볼)
const setFavicon = (href: string) => {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
  if (link) link.href = href;
};

// marked 설정
marked.setOptions({
  breaks: false,
  gfm: true,
});

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
  chartData?: Record<string, unknown>[];
}

interface RecentChat {
  workflowId: string;
  workflowName: string;
  lastMessage: string;
  timestamp: Date;
}

type PortalView = 'home' | 'agents' | 'workflows' | 'marketplace' | 'templates' | 'knowledge';

// 봇 아이콘 경로 헬퍼
const getBotIconPath = (style?: 'white' | 'black') => {
  return style === 'black' ? '/icons8-chatbot-32_white.png' : '/icons8-chatbot-32.png';
};

// 모델 이름 매핑 (Bedrock 전체 모델 ID)
const MODEL_NAMES: Record<string, string> = {
  // Amazon Nova (APAC)
  'apac.amazon.nova-lite-v1:0': 'Nova Lite',
  'apac.amazon.nova-micro-v1:0': 'Nova Micro',
  'apac.amazon.nova-pro-v1:0': 'Nova Pro',
  // Amazon Nova (Global)
  'global.amazon.nova-2-lite-v1:0': 'Nova 2 Lite',
  // Claude (APAC)
  'apac.anthropic.claude-sonnet-4-20250514-v1:0': 'Claude Sonnet 4',
  // Claude (Direct)
  'anthropic.claude-opus-5': 'Claude Opus 5',
  'anthropic.claude-sonnet-5': 'Claude Sonnet 5',
  // Claude (Global)
  'global.anthropic.claude-fable-5': 'Claude Fable 5',
  'global.anthropic.claude-fable-5-1': 'Claude Fable 5.1',
  'global.anthropic.claude-haiku-4-5-20251001-v1:0': 'Claude Haiku 4.5',
  'global.anthropic.claude-haiku-5-5': 'Claude Haiku 5.5',
  'global.anthropic.claude-opus-4-5-20251101-v1:0': 'Claude Opus 4.5',
  'global.anthropic.claude-opus-4-6-v1': 'Claude Opus 4.6',
  'global.anthropic.claude-opus-4-7': 'Claude Opus 4.7',
  'global.anthropic.claude-opus-4-8': 'Claude Opus 4.8',
  'global.anthropic.claude-opus-5-5': 'Claude Opus 5.5',
  'global.anthropic.claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'global.anthropic.claude-sonnet-5-5': 'Claude Sonnet 5.5',
  'global.anthropic.claude-sonnet-4-5-20250514-v1:0': 'Claude Sonnet 4.5',
  // OpenAI (Global)
  'global.openai.gpt-5.4': 'GPT-5.4',
  'global.openai.gpt-5.5': 'GPT-5.5',
  'global.openai.gpt-5.6-luna': 'GPT-5.6 Luna',
  'global.openai.gpt-5.6-sol': 'GPT-5.6 Sol',
  'global.openai.gpt-5.6-terra': 'GPT-5.6 Terra',
  'global.openai.gpt-6-astra': 'GPT-6 Astra',
  'global.openai.gpt-6-luna': 'GPT-6 Luna',
  'global.openai.gpt-6-sol': 'GPT-6 Sol',
  'global.openai.gpt-6.1-sol': 'GPT-6.1 Sol',
};

// LLM 노드에서 모델/온도/제공자 추출
const getLLMConfig = (workflow: Workflow | null) => {
  if (!workflow) return { model: '', models: [] as string[], temperature: '0.7', provider: 'bedrock' };
  const llmNode = workflow.nodes?.find((n) => n.type === 'llm');
  const rawModels = llmNode?.data?.models as string[] | undefined;
  const legacyModel = llmNode?.data?.model as string | undefined;
  const models: string[] = rawModels?.length ? rawModels : (legacyModel ? [legacyModel] : []);
  return {
    model: models[0] || '',
    models,
    temperature: (llmNode?.data?.temperature as string) || '0.7',
    provider: (llmNode?.data?.provider as string) || 'bedrock',
  };
};

const PROVIDER_LABELS: Record<string, { label: string; icon: string }> = {
  bedrock:    { label: 'Bedrock',    icon: '☁️' },
  anthropic:  { label: 'Anthropic',  icon: '🤖' },
  openai:     { label: 'OpenAI',     icon: '🟢' },
};

/** 모델 ID → 표시 이름 */
function getModelName(modelId: string): string {
  if (!modelId) return '';
  if (MODEL_NAMES[modelId]) return MODEL_NAMES[modelId];
  const id = modelId.toLowerCase();
  if (id.includes('nova-2-lite'))       return 'Nova 2 Lite';
  if (id.includes('nova-2-micro'))      return 'Nova 2 Micro';
  if (id.includes('nova-2-pro'))        return 'Nova 2 Pro';
  if (id.includes('nova-lite'))         return 'Nova Lite';
  if (id.includes('nova-micro'))        return 'Nova Micro';
  if (id.includes('nova-pro'))          return 'Nova Pro';
  if (id.includes('claude-sonnet-4-5')) return 'Claude Sonnet 4.5';
  if (id.includes('claude-haiku-4-5'))  return 'Claude Haiku 4.5';
  if (id.includes('claude-opus-4-5'))   return 'Claude Opus 4.5';
  if (id.includes('claude-sonnet-4'))   return 'Claude Sonnet 4';
  if (id.includes('claude-haiku-4'))    return 'Claude Haiku 4';
  if (id.includes('claude-opus-4'))     return 'Claude Opus 4';
  if (id.includes('claude-sonnet-5'))   return 'Claude Sonnet 5';
  if (id.includes('claude-haiku-5'))    return 'Claude Haiku 5';
  if (id.includes('claude-opus-5'))     return 'Claude Opus 5';
  if (id.includes('claude-fable-5-1'))  return 'Claude Fable 5.1';
  if (id.includes('claude-fable-5'))    return 'Claude Fable 5';
  if (id.includes('claude-sonnet'))     return 'Claude Sonnet';
  if (id.includes('claude-haiku'))      return 'Claude Haiku';
  if (id.includes('claude-opus'))       return 'Claude Opus';
  const gptM = id.match(/gpt-(\d+(?:\.\d+)?(?:-\w+)?)/);
  if (gptM) return `GPT-${gptM[1]}`;
  return modelId;
}

// 스케줄 실행 시간 포맷
const formatScheduleTime = (workflow: Workflow): string => {
  const node = workflow.nodes.find(n => n.type === 'schedule-trigger');
  if (!node) return '';
  const d = node.data as Record<string, unknown>;
  const type = (d.scheduleType as string) || 'daily';
  if (type === 'interval') return `${d.interval || 5}분 간격`;
  if (type === 'hourly') return `매시 ${String(d.hourlyMinute ?? 0).padStart(2,'0')}분`;
  if (type === 'daily') {
    const h = String(d.dailyHour ?? 9).padStart(2,'0');
    const m = String(d.dailyMinute ?? 0).padStart(2,'0');
    return `매일 ${h}:${m}`;
  }
  if (type === 'weekly') {
    const days = ['일','월','화','수','목','금','토'];
    const day = days[Number(d.weeklyDay ?? 1)];
    const h = String(d.weeklyHour ?? 9).padStart(2,'0');
    const m = String(d.weeklyMinute ?? 0).padStart(2,'0');
    return `매주 ${day}요일 ${h}:${m}`;
  }
  return '';
};

// URL 경로에서 뷰 추출
const getViewFromPath = (pathname: string): PortalView => {
  if (pathname === '/portal/agents') return 'agents';
  return 'home';
};

export function PortalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = authApi.getUser();

  // URL 경로에서 초기 뷰 설정
  const [activeView, setActiveView] = useState<PortalView>(() => getViewFromPath(location.pathname));
  const [chatbots, setChatbots] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeScheduleIds, setActiveScheduleIds] = useState<Set<string>>(new Set());

  // 채팅 상태
  const [selectedChatbot, setSelectedChatbot] = useState<Workflow | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingStatus, setStreamingStatus] = useState('');
  const [streamingSources, setStreamingSources] = useState<ChatSource[]>([]);
  const [streamingChartData, setStreamingChartData] = useState<Record<string, unknown>[] | null>(null);
  const [recentChats, setRecentChats] = useState<RecentChat[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // DDB 세션 상태
  const [sessionId, setSessionId] = useState<string>(() => nanoid(10));
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [chatIdCounter, setChatIdCounter] = useState(1);

  // URL 경로 변경 시 뷰 동기화
  useEffect(() => {
    const newView = getViewFromPath(location.pathname);
    setActiveView(newView);
  }, [location.pathname]);

  // 뷰 변경 핸들러 (URL도 함께 변경)
  const handleViewChange = (view: PortalView) => {
    setActiveView(view);
    if (view === 'home') {
      navigate('/portal');
    } else if (view === 'agents') {
      navigate('/portal/agents');
    }
  };

  useEffect(() => {
    loadChatbots();
    loadRecentChats();

    // 사용자 포탈 진입 시 사용자 심볼 파비콘 (관리자가 들어와도 포탈은 사용자 화면)
    setFavicon('/favicon-user.svg');

    // 나갈 때 로그인 역할 파비콘으로 복원 (authApi.applyRoleTheme 과 같은 규칙)
    return () => {
      setFavicon(`/favicon-${document.documentElement.dataset.role === 'admin' ? 'admin' : 'user'}.svg`);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing, streamingContent]);

  const loadChatbots = async () => {
    try {
      setLoading(true);
      const data = await workflowApi.list({ deployed: true });
      // 챗봇 공유된 것만 포탈에 표시
      setChatbots(data.filter(w => w.isSharedAsChatbot === true));
      // 활성 스케줄 조회
      try {
        const ids = await workflowApi.getActiveSchedules();
        setActiveScheduleIds(new Set(ids));
      } catch { /* 무시 */ }
    } catch (error) {
      console.error('Failed to load chatbots:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentChats = () => {
    const stored = localStorage.getItem('portal_recent_chats');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentChats(parsed.slice(0, 5));
      } catch {
        setRecentChats([]);
      }
    }
  };

  const saveRecentChat = (workflow: Workflow, lastMessage: string) => {
    const newChat: RecentChat = {
      workflowId: workflow.id,
      workflowName: workflow.name,
      lastMessage: lastMessage.substring(0, 50) + (lastMessage.length > 50 ? '...' : ''),
      timestamp: new Date(),
    };

    const updated = [newChat, ...recentChats.filter((c) => c.workflowId !== workflow.id)].slice(0, 5);
    setRecentChats(updated);
    localStorage.setItem('portal_recent_chats', JSON.stringify(updated));
  };

  const handleSelectChatbot = (workflow: Workflow) => {
    setSelectedChatbot(workflow);
    setMessages([]);
    setSessionId(nanoid(10));
    setCurrentSessionId(null);
    setChatIdCounter(1);
    setActiveView('agents');
  };

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || !selectedChatbot || typing) return;

    const msgId = nanoid(10);
    const startedAt = performance.now();

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setTyping(true);
    setStreamingContent('');
    setStreamingStatus('');
    setStreamingSources([]);

    // 세션 저장 (사용자 메시지)
    const llmNode = selectedChatbot?.nodes?.find(n => n.type === 'llm');
    const modelId = (llmNode?.data?.model as string) || '';
    const startTime = new Date().toISOString();
    let activeSessionId = currentSessionId;
    const currentChatId = chatIdCounter;

    try {
      const saved = await chatSessionApi.saveUserMessage({
        sessionId: activeSessionId || sessionId,
        chatId: String(currentChatId),
        workflowId: selectedChatbot.id,
        userContent: messageText,
        startDateTime: startTime,
        modelId,
        themeMode: 'light',
        messageId: msgId,
      });
      if (!activeSessionId) {
        activeSessionId = saved.sessionId;
        setCurrentSessionId(saved.sessionId);
      }
      // 첫 질문이면 title 자동 설정
      if (currentChatId === 1) {
        const title = messageText.slice(0, 30) + (messageText.length > 30 ? '...' : '');
        await chatSessionApi.renameSession(activeSessionId, title).catch(() => {});
      }
    } catch { /* 저장 실패 무시 */ }

    try {
      const history: ApiChatMessage[] = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // 스트리밍 API 호출 - 개발환경에서 Vite proxy 버퍼링 우회, 직접 3002 연결
      const SSE_BASE = import.meta.env.DEV ? 'http://localhost:3002' : '';
      const response = await fetch(`${SSE_BASE}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('agentbuilder_token')}`,
        },
        body: JSON.stringify({
          workflowId: selectedChatbot.id,
          message: messageText,
          history,
        }),
      });

      if (!response.ok) {
        throw new Error('스트리밍 요청 실패');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let sources: ChatSource[] = [];
      let chartData: Record<string, unknown>[] | null = null;
      let usage: { inputToken: number; outputToken: number; totalToken: number } | null = null;
      let streamError: Error | null = null;

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (data.type === 'text') {
                  fullContent += data.data;
                  setStreamingContent(fullContent);
                  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
                } else if (data.type === 'status') {
                  setStreamingStatus(data.data as string);
                  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
                } else if (data.type === 'sources') {
                  sources = data.data;
                  setStreamingSources(sources);
                } else if (data.type === 'chart') {
                  chartData = data.data as Record<string, unknown>[];
                  setStreamingChartData(chartData);
                } else if (data.type === 'usage') {
                  usage = data.data as { inputToken: number; outputToken: number; totalToken: number };
                } else if (data.type === 'error') {
                  streamError = new Error(typeof data.data === 'string' ? data.data : '오류가 발생했습니다.');
                }
              } catch {
                // JSON 파싱 에러 무시
              }
            }
          }
          if (streamError) throw streamError;
        }
      }

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: fullContent,
        sources,
        chartData: chartData ?? undefined,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setStreamingContent('');
      setStreamingSources([]);
      setStreamingChartData(null);

      saveRecentChat(selectedChatbot, messageText);

      // AI 응답 DDB 저장
      if (activeSessionId) {
        try {
          await chatSessionApi.saveAssistantMessage({
            sessionId: activeSessionId,
            chatId: String(currentChatId),
            assistantContent: fullContent,
            endDateTime: new Date().toISOString(),
            inputToken: usage?.inputToken,
            outputToken: usage?.outputToken,
            totalToken: usage?.totalToken,
            resultTime: ((performance.now() - startedAt) / 1000).toFixed(1),
            status: 'complete',
            messageId: msgId,
            sources,
          });
          setChatIdCounter(prev => prev + 1);
        } catch { /* 저장 실패 무시 */ }
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      const errContent = `⚠️ 오류가 발생했습니다.\n\n${errMsg}`;
      setStreamingContent('');
      setStreamingSources([]);
      setStreamingChartData(null);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: errContent,
        },
      ]);
      // 에러 내용도 DDB에 저장
      if (activeSessionId) {
        chatSessionApi.saveAssistantMessage({
          sessionId: activeSessionId,
          chatId: String(currentChatId),
          assistantContent: errContent,
          endDateTime: new Date().toISOString(),
          status: 'error',
          messageId: msgId,
        }).catch(() => {});
      }
    } finally {
      setTyping(false);
    }
  };

  const renderMarkdown = (content: string) => {
    return { __html: marked.parse(content) as string };
  };

  const filteredChatbots = chatbots.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClearChat = () => {
    setMessages([]);
    setSessionId(nanoid(10));
    setCurrentSessionId(null);
    setChatIdCounter(1);
  };

  // 챗봇 선택 시 전체 화면 챗봇 UI
  if (selectedChatbot) {
    return (
      <div className="portal-shell">
        <PortalSidebar
          activeView={activeView}
          onViewChange={(view) => { setSelectedChatbot(null); handleViewChange(view); }}
          recentChats={recentChats}
          chatbots={chatbots}
          onSelectChatbot={(bot) => { if (bot) handleSelectChatbot(bot); else setSelectedChatbot(null); }}
          selectedChatbot={selectedChatbot}
        />

        <div className="portal-main portal-chat-main">
          {messages.length === 0 && !typing ? (
            /* 대화 시작 전: 중앙 레이아웃 */
            <div className="portal-chat-welcome-screen" style={{ background: selectedChatbot.theme?.backgroundColor || '#f8fafc' }}>
              <div className="portal-chat-welcome-center">
                <div className="portal-chat-welcome-icon bot-icon-wrap" style={{ background: selectedChatbot.theme?.primaryColor || '#6366f1' }}>
                  <img src={getBotIconPath(selectedChatbot.theme?.botIconStyle)} alt="bot" className="bot-icon-lg" />
                </div>
                <h2>{selectedChatbot.theme?.botName || selectedChatbot.name}</h2>
                <p>{selectedChatbot.theme?.welcomeMessage || '무엇을 도와드릴까요?'}</p>

                <div className="portal-chat-llm-info">
                  <span className="portal-chat-llm-badge">
                    🏢 {PROVIDER_LABELS[getLLMConfig(selectedChatbot).provider]?.label || getLLMConfig(selectedChatbot).provider}
                  </span>
                  <span className="portal-chat-llm-badge">
                    {PROVIDER_LABELS[getLLMConfig(selectedChatbot).provider]?.icon || '🤖'} {getModelName(getLLMConfig(selectedChatbot).model)}
                  </span>
                  <span className="portal-chat-llm-badge">
                    🌡️ Temperature: {getLLMConfig(selectedChatbot).temperature}
                  </span>
                </div>

                <div className="portal-chat-welcome-input">
                  <div className="portal-chat-input-wrapper">
                    <input
                      type="text"
                      className="portal-chat-input-field"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      placeholder="메시지를 입력하세요..."
                      disabled={typing}
                      autoFocus
                    />
                    <button
                      className="portal-chat-send-btn"
                      onClick={() => handleSend()}
                      disabled={!input.trim() || typing}
                      style={{ background: selectedChatbot.theme?.primaryColor || '#6366f1' }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" />
                      </svg>
                    </button>
                  </div>
                </div>

                {(selectedChatbot.theme?.quickPrompts || []).filter(p => p.trim()).length > 0 && (
                  <div className="portal-chat-quick-prompts">
                    {selectedChatbot.theme!.quickPrompts!.filter(p => p.trim()).map((prompt, i) => (
                      <button
                        key={i}
                        className="portal-chat-quick-btn"
                        onClick={() => handleSend(prompt)}
                        style={{
                          borderColor: selectedChatbot.theme?.primaryColor || '#6366f1',
                          color: selectedChatbot.theme?.primaryColor || '#6366f1',
                        }}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* 대화 중: 메시지 + 하단 입력창 */
            <div className="portal-chat-conversation">
              <div className="portal-chat-header" style={{ background: selectedChatbot.theme?.primaryColor || '#6366f1' }}>
                <div className="portal-chat-header-left">
                  <span className="portal-chat-header-title">{selectedChatbot.theme?.botName || selectedChatbot.name}</span>
                  <span className="portal-chat-header-llm">
                    {getModelName(getLLMConfig(selectedChatbot).model)} · {getLLMConfig(selectedChatbot).provider === 'anthropic' ? 'Anthropic' : 'Bedrock'} · T:{getLLMConfig(selectedChatbot).temperature}
                  </span>
                </div>
                <button className="portal-chat-clear-btn" onClick={handleClearChat}>
                  대화 초기화
                </button>
              </div>

              <div className="portal-chat-messages" style={{ background: selectedChatbot.theme?.backgroundColor || '#f8fafc' }}>
                <div className="portal-chat-messages-inner">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`portal-chat-message ${msg.role}`}>
                      <div
                        className="portal-chat-avatar"
                        style={msg.role === 'user'
                          ? { background: selectedChatbot.theme?.userBubbleColor || '#e0e7ff' }
                          : { background: selectedChatbot.theme?.primaryColor || '#6366f1' }
                        }
                      >
                        {msg.role === 'user' ? '👤' : <img src={getBotIconPath(selectedChatbot.theme?.botIconStyle)} alt="bot" className="bot-icon-sm" />}
                      </div>
                      <div className="portal-chat-content">
                        {msg.role === 'user' ? (
                          <div
                            className="portal-chat-bubble user"
                            style={{
                              background: selectedChatbot.theme?.userBubbleColor || '#e0e7ff',
                              color: selectedChatbot.theme?.userTextColor || '#1e1b4b',
                            }}
                          >{msg.content}</div>
                        ) : (
                          <div
                            className="portal-chat-bubble bot markdown-content"
                            style={{
                              background: selectedChatbot.theme?.botBubbleColor || '#ffffff',
                              color: selectedChatbot.theme?.botTextColor || '#374151',
                            }}
                            dangerouslySetInnerHTML={renderMarkdown(msg.content)}
                          />
                        )}
                        {msg.sources && msg.sources.length > 0 && (() => {
                          const sqlSources = msg.sources.filter(s => s.documentName === 'Generated SQL' && s.excerpt?.trim());
                          const refSources = msg.sources.filter(s => s.documentName !== 'Generated SQL');
                          if (sqlSources.length === 0 && refSources.length === 0) return null;
                          return (
                            <div className="portal-chat-sources">
                              {sqlSources.length > 0
                                ? sqlSources.map((s, i) => <SqlBlock key={i} sql={s.excerpt || ''} />)
                                : (
                                  <>
                                    <span className="portal-sources-label">📎 출처:</span>
                                    {refSources.map((s, i) => (
                                      s.url ? (
                                        <a key={i} className="portal-source-tag" href={s.url} target="_blank" rel="noopener noreferrer" title={s.url}>{s.documentName}</a>
                                      ) : (
                                        <span key={i} className="portal-source-tag">{s.documentName}</span>
                                      )
                                    ))}
                                  </>
                                )
                              }
                            </div>
                          );
                        })()}
                        {/* 차트 렌더링 */}
                        {msg.chartData && msg.chartData.length > 0 && (
                          <ChartBlock rows={msg.chartData} />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* 스트리밍 중인 메시지 */}
                  {typing && (
                    <div className="portal-chat-message assistant">
                      <div className="portal-chat-avatar" style={{ background: selectedChatbot.theme?.primaryColor || '#6366f1' }}>
                        <img src={getBotIconPath(selectedChatbot.theme?.botIconStyle)} alt="bot" className="bot-icon-sm" />
                      </div>
                      <div className="portal-chat-content">
                        {streamingContent ? (
                          <>
                            <div
                              className="portal-chat-bubble bot markdown-content"
                              style={{
                                background: selectedChatbot.theme?.botBubbleColor || '#ffffff',
                                color: selectedChatbot.theme?.botTextColor || '#374151',
                              }}
                              dangerouslySetInnerHTML={renderMarkdown(streamingContent)}
                            />
                            {streamingSources.length > 0 && (() => {
                              const sqlSources = streamingSources.filter(s => s.documentName === 'Generated SQL' && s.excerpt?.trim());
                              const refSources = streamingSources.filter(s => s.documentName !== 'Generated SQL');
                              if (sqlSources.length === 0 && refSources.length === 0) return null;
                              return (
                                <div className="portal-chat-sources">
                                  {sqlSources.length > 0
                                    ? sqlSources.map((s, i) => <SqlBlock key={i} sql={s.excerpt || ''} />)
                                    : (
                                      <>
                                        <span className="portal-sources-label">📎 출처:</span>
                                        {refSources.map((s, i) => (
                                          s.url ? (
                                            <a key={i} className="portal-source-tag" href={s.url} target="_blank" rel="noopener noreferrer" title={s.url}>{s.documentName}</a>
                                          ) : (
                                            <span key={i} className="portal-source-tag">{s.documentName}</span>
                                          )
                                        ))}
                                      </>
                                    )
                                  }
                                </div>
                              );
                            })()}
                            {/* 스트리밍 중 차트 */}
                            {streamingChartData && streamingChartData.length > 0 && (
                              <ChartBlock rows={streamingChartData} />
                            )}
                          </>
                        ) : streamingStatus ? (
                          <div className="portal-chat-typing-status">{streamingStatus}</div>
                        ) : (
                          <div className="portal-chat-typing">
                            <span></span><span></span><span></span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="portal-chat-input-area">
                <div className="portal-chat-input-container">
                  <div className="portal-chat-input-wrapper">
                    <input
                      type="text"
                      className="portal-chat-input-field"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      placeholder="메시지를 입력하세요..."
                      disabled={typing}
                    />
                    <button
                      className="portal-chat-send-btn"
                      onClick={() => handleSend()}
                      disabled={!input.trim() || typing}
                      style={{ background: selectedChatbot.theme?.primaryColor || '#6366f1' }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="portal-shell">
      <PortalSidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        recentChats={recentChats}
        chatbots={chatbots}
        onSelectChatbot={(bot) => { if (bot) handleSelectChatbot(bot); }}
        selectedChatbot={selectedChatbot}
      />

      <div className="portal-main">
        {/* 홈 뷰 */}
        {activeView === 'home' && (
          <div className="portal-view">
            <div className="portal-hero">
              <div className="portal-hero-greeting">안녕하세요, {user?.name || '사용자'}님</div>
              <h1>오늘의 <span className="portal-hero-highlight">현황</span>이에요</h1>
            </div>

            <div className="portal-home-stats">
              <div className="portal-stat-card">
                <div className="portal-stat-icon">💬</div>
                <div className="portal-stat-info">
                  <div className="portal-stat-value">
                    {chatbots.filter(w => w.nodes.some(n => n.type === 'trigger') || !w.nodes.some(n => ['schedule-trigger','mail-trigger','webhook-trigger','google-drive','notion','gmail','naver'].includes(n.type))).length}
                  </div>
                  <div className="portal-stat-label">사용 가능한 챗봇</div>
                </div>
              </div>
              <div className="portal-stat-card">
                <div className="portal-stat-icon">⏰</div>
                <div className="portal-stat-info">
                  <div className="portal-stat-value">{activeScheduleIds.size}</div>
                  <div className="portal-stat-label">실행 중인 스케줄</div>
                </div>
              </div>
            </div>

            <div className="portal-home-section">
              <div className="portal-section-label-row">
                <span className="portal-section-label">사용 가능한 챗봇</span>
                <button className="portal-section-more" onClick={() => handleViewChange('agents')}>
                  더보기 →
                </button>
              </div>
              {loading ? (
                <div className="portal-loading">로딩 중...</div>
              ) : chatbots.filter(w => !w.nodes.some(n => ['schedule-trigger','mail-trigger','webhook-trigger','google-drive','notion','gmail','naver'].includes(n.type))).length === 0 ? (
                <div className="portal-empty">배포된 챗봇이 없습니다.</div>
              ) : (
                <div className="portal-grid">
                  {chatbots
                    .filter(w => !w.nodes.some(n => ['schedule-trigger','mail-trigger','webhook-trigger','google-drive','notion','gmail','naver'].includes(n.type)))
                    .sort((a, b) => new Date(b.deployedAt || b.createdAt).getTime() - new Date(a.deployedAt || a.createdAt).getTime())
                    .slice(0, 6)
                    .map((chatbot) => (
                    <div key={chatbot.id} className="portal-card">
                      <div className="portal-card-top">
                        <div className="portal-card-icon">
                          <img src={getBotIconPath(chatbot.theme?.botIconStyle)} alt="bot" className="bot-icon-md" />
                        </div>
                        <div>
                          <div className="portal-card-title">{chatbot.name}</div>
                          <div className="portal-card-meta">활성</div>
                        </div>
                      </div>
                      <div className="portal-card-desc">{chatbot.description || '설명 없음'}</div>
                      <button className="portal-open-btn" onClick={() => handleSelectChatbot(chatbot)}>
                        대화하기
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 실행 중인 스케줄 목록 */}
            {activeScheduleIds.size > 0 && (
              <div className="portal-home-section">
                <div className="portal-section-label">실행 중인 스케줄</div>
                <div className="portal-schedule-list">
                  {chatbots
                    .filter(w => activeScheduleIds.has(w.id))
                    .map(w => (
                      <div key={w.id} className="portal-schedule-item">
                        <span className="portal-schedule-dot" />
                        <span
                          className="portal-schedule-name portal-schedule-link"
                          onClick={() => navigate(`/workflows/${w.id}`)}
                        >{w.name}</span>
                        <span className="portal-schedule-time">{formatScheduleTime(w)}</span>
                        {w.ownerName && <span className="portal-schedule-owner">{w.ownerName}</span>}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 챗봇 뷰 */}
        {activeView === 'agents' && (
          <div className="portal-view">
            <div className="portal-view-header">
              <div className="portal-view-title">챗봇</div>
              <div className="portal-view-sub">사용할 수 있는 사내 챗봇이에요. 아래에서 골라 대화해보세요.</div>
            </div>

            {!selectedChatbot ? (
              <>
                <div className="portal-agent-toolbar">
                  <input
                    type="text"
                    className="portal-search-input"
                    placeholder="챗봇 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <div className="portal-chatcard-grid">
                  {filteredChatbots.map((chatbot) => (
                    <div key={chatbot.id} className="portal-chatcard active">
                      <div className="portal-chatcard-badge">
                        <span className="portal-badge-official">회사 공식</span>
                        <div className="portal-chatcard-badge-right">
                          <span className="portal-badge-active">활성</span>
                        </div>
                      </div>
                      <div className="portal-chatcard-icon-wrap">
                        <div className="portal-chatcard-icon">
                          <img src={getBotIconPath(chatbot.theme?.botIconStyle)} alt="bot" className="bot-icon-card" />
                        </div>
                      </div>
                      <div className="portal-chatcard-body">
                        <div className="portal-chatcard-name">{chatbot.name}</div>
                        <div className="portal-chatcard-desc">{chatbot.description || '설명 없음'}</div>
                        {chatbot.ownerName && (
                          <div className="portal-chatcard-owner">만든 사람: {chatbot.ownerName}</div>
                        )}
                      </div>
                      <div className="portal-chatcard-actions">
                        <button
                          className="portal-chatcard-btn active"
                          onClick={() => handleSelectChatbot(chatbot)}
                        >
                          💬 대화하기
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalPage;
